import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { handleThunkError } from '../common/util';
import { ConfigName, updateConfigObject } from '../sqlite/common/config';
import { RootState } from '.';
import { IOdataResult } from '../models/odata.model';
import { setIsLoadingCountState } from './appSlice';
import { logConsole, logTransaction } from '../common/log';
import { getTechWorkCategories } from '../api/dms/technician.api';
import { IWorkCategory, IWorkCategorySyncStatus, UNKNOWN_TECH } from '../models/dms/technician.model';
import { LogType } from '../models/log.model';

interface IWorkCategorySyncState {
  workCategoriesSync?: IWorkCategorySyncStatus;
  workCategoriesSyncLoading: boolean;
}

const initialState: IWorkCategorySyncState = {
  workCategoriesSync: {
    hasSync: false,
    timestamp: 0,
    isProcessing: false,
    data: {} as IOdataResult<IWorkCategory>
  },
  workCategoriesSyncLoading: false,
};

/// THUNKS

export const setWorkCategoriesSyncStoreAsync = createAsyncThunk<{}, IWorkCategorySyncStatus | undefined>(
  'workCategorySync/setWorkCategoriesSyncStoreAsync',
  async (val, { rejectWithValue, dispatch }) => {
    logTransaction(LogType.Info, `Setting work categories to storage`, '[workCategorySync] setWorkCategoriesSyncStoreAsync');
    try {
      dispatch(setWorkCategoriesSyncState(val));
      await updateConfigObject<IWorkCategorySyncStatus>(ConfigName.WorkCategoriesSync, val || {} as IWorkCategorySyncStatus, false);
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  }
);

export const setWorkCategoriesProcessingStoreAsync = createAsyncThunk<{}, boolean>(
  'workCategorySync/setWorkCategoriesProcessingAsync',
  async (val, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, '', '[workCategorySync] setWorkCategoriesProcessingAsync');
    try {
      dispatch(setWorkCategoriesProcessingState(val));
      await updateConfigObject<IWorkCategorySyncStatus>(ConfigName.WorkCategoriesSync, {
        ...(getState() as RootState).workCategorySync.workCategoriesSync,
        isProcessing: val
      } as IWorkCategorySyncStatus, false);
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  }
);

export const setWorkCategoriesHasSyncStoreAsync = createAsyncThunk<{}, boolean>(
  'workCategorySync/setWorkCategoriesHasSyncStoreAsync',
  async (val, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, '', '[workCategorySync] setWorkCategoriesHasSyncStoreAsync');
    try {
      dispatch(setWorkCategoriesHasSyncState(val));
      await updateConfigObject<IWorkCategorySyncStatus>(ConfigName.WorkCategoriesSync, {
        ...(getState() as RootState).workCategorySync.workCategoriesSync,
        hasSync: val,
        isProcessing: false,
      } as IWorkCategorySyncStatus, false);
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  }
);

export const fetchWorkCategoriesStoreAsync = createAsyncThunk<{}, void>(
  'workCategorySync/fetchWorkCategoriesStoreAsync',
  async (nil, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, `Fetching work categories from database`, '[workCategorySync] fetchWorkCategoriesStoreAsync');
    try {
      dispatch(setIsLoadingCountState(1));
      dispatch(setWorkCategoriesSyncLoadingState(true));

      const state = (getState() as RootState);
      const { workCategoriesSync } = state.workCategorySync;
      const { technician } = (getState() as RootState).auth;

      let workCategoriesSyncState = { ...workCategoriesSync } as IWorkCategorySyncStatus;

      try {
        const responseData = await getTechWorkCategories(technician?.code || UNKNOWN_TECH);

        workCategoriesSyncState = {
          ...workCategoriesSyncState,
          timestamp: new Date().getTime(),
          hasSync: true,
          isProcessing: false,
          data: { ...responseData }
        };

        dispatch(setWorkCategoriesSyncState(workCategoriesSyncState));
        await updateConfigObject<IWorkCategorySyncStatus>(ConfigName.WorkCategoriesSync, workCategoriesSyncState, false);
        return responseData;
      } catch (ex: any) {
        logTransaction(LogType.Error, `Failed to execute fetchWorkCategoriesStoreAsync thunk. ${ex?.message}`);
      } finally {
        dispatch(setIsLoadingCountState(-1));
        dispatch(setWorkCategoriesSyncLoadingState(false));
      }

    } catch (err: any) {
      return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setWorkCategoriesSyncLoadingState(false));
    }
  }
);

/// SLICE

export const workCategorySyncSlice = createSlice({
  name: 'workCategorySync',
  initialState,
  reducers: {
    setWorkCategoriesSyncState: (state, action: PayloadAction<IWorkCategorySyncStatus | undefined>) => {
      logConsole(LogType.Info, '[workCategorySync] setWorkCategoriesSyncState');
      state.workCategoriesSync = action.payload;
    },
    setWorkCategoriesProcessingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[workCategorySync] setWorkCategoriesProcessingState: ${action.payload}`);
      if (state.workCategoriesSync) {
        state.workCategoriesSync.isProcessing = action.payload;
      }
    },
    setWorkCategoriesHasSyncState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[workCategorySync] setWorkCategoriesHasSyncState: ${action.payload}`);
      if (state.workCategoriesSync) {
        state.workCategoriesSync.hasSync = action.payload;
        state.workCategoriesSync.isProcessing = false;
      }
    },
    setWorkCategoriesSyncLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[workCategorySync] setWorkCategoriesSyncLoadingState: ${action.payload}`);
      state.workCategoriesSyncLoading = action.payload;
    },
  },
});

/// REDUCERS

export const { setWorkCategoriesSyncState, setWorkCategoriesProcessingState, setWorkCategoriesHasSyncState, setWorkCategoriesSyncLoadingState } = workCategorySyncSlice.actions;

export default workCategorySyncSlice.reducer;
